import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, TextField, Chip } from '@material-ui/core'
import EmailIcon from 'web/src/assets/svg/email-icon.svg'
import { makeStyles } from '@material-ui/core'
import { Input } from 'web/src/app/root/components/common'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import { IconButton } from '@material-ui/core'
import RemoveIconSvg from 'web/src/assets/svg/remove-icon.svg'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Cancel from '@material-ui/icons/Cancel'
import { Tooltip, Checkbox } from 'web/src/app/root/components/common'
import { ESCALATION_LEVELS_REPORT_TYPES } from 'web/src/app/root/constants'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import { Info } from 'lucide-react'
import { cloneDeep } from 'lodash'

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedCheckboxIcon = <CheckBoxIcon fontSize="small" />

const useStyles = makeStyles(() => ({
    content: {
        display: 'flex'
    },

    formWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        flex: 1
    },
    daysWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },

    textItalic: {
        fontStyle: 'italic'
    },

    textBold: {
        fontWeight: '600'
    },
    shake: {
        animation: '$shake 1s ease-in-out',
        margin: 2
    },
    '@keyframes shake': {
        '10%, 90%': {
            transform: 'translate3d(-1px, 0, 0)'
        },
        '20%, 80%': {
            transform: 'translate3d(2px, 0, 0)'
        },
        '30%, 50%, 70%': {
            transform: 'translate3d(-4px, 0, 0)'
        },
        '40%, 60%': {
            transform: 'translate3d(4px, 0, 0)'
        }
    },
    marginChip: {
        margin: 2
    }
}))

export const LevelComponent = ({
    level,
    email,
    message,
    daysUntilAlert,
    hoursUntilAlert,
    setEscalationLevelsState,
    onDeleteLevel,
    daysUntilAlertMin,
    hoursUntilAlertMin,
    reportTypes,
    escalationLevelsState,
    fullErrorState,
    errorState,
    setErrorState
}) => {
    const classes = useStyles()
    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)
    const [emails, setEmails] = useState([email])
    const [emailErrors, setEmailErrors] = useState([])
    const emailValidationSchema = yup
        .string()
        .email(locale.detachedSharing.plsTypeInEmail)
        .required(locale.userManagement.emailIsEmpty)
    const emailValidatorAndHandler = emails => {
        const emailString = emails.join(',')
        setEscalationLevelsState(levels => {
            const filteredLevels = JSON.parse(JSON.stringify(levels)).map(
                item => {
                    if (item.level === level) {
                        item.email = emailString
                        return item
                    }
                    return item
                }
            )
            return filteredLevels
        })
    }

    const reportTypesOptions = useMemo(() => {
        return ESCALATION_LEVELS_REPORT_TYPES.map(reportType => {
            const name =
                Number(reportType) === 3
                    ? `${locale.devices.deviceLastReportTypeShortDescription[reportType]}, ${locale.devices.deviceLastReportTypeShortDescription['3-bait']}`
                    : locale.devices.deviceLastReportTypeShortDescription[
                          reportType
                      ]
            return {
                reportType,
                name
            }
        })
    }, [locale])

    const handleEmailInputChange = e => {
        if (errorState.email) {
            const newErrorState = cloneDeep(errorState)
            newErrorState.email = false
            setErrorState({
                ...fullErrorState,
                [level]: newErrorState
            })
        }
        const value = e.target.value.trim()
        e.preventDefault()
        if (e.key === 'Enter' || e.type === 'blur') {
            if (value !== '') {
                const newEmails = [
                    ...emails,
                    ...value
                        .split(',')
                        .map(e => e.trim())
                        .filter(e => e !== '')
                ]
                updateEmailsAndErrors(newEmails)
            }
            e.target.value = ''
        }
    }

    const updateEmailsAndErrors = emails => {
        const trimmedEmails = emails.map(e => e.trim())
        const newEmailErrors = trimmedEmails.map(email => {
            try {
                emailValidationSchema.validateSync(email)
                return null
            } catch (error) {
                return error.message
            }
        })
        setEmails(trimmedEmails)
        setEmailErrors(newEmailErrors)
        emailValidatorAndHandler(trimmedEmails)
    }

    const handleDaysChange = e => {
        const value = Number(e.target.value)
        let newEscalationLevelsState = cloneDeep(escalationLevelsState)
        newEscalationLevelsState = newEscalationLevelsState.map(item => {
            if (item.level === level && value >= daysUntilAlertMin) {
                item.daysUntilAlert = value
            }
            return item
        })
        setEscalationLevelsState(newEscalationLevelsState)
    }

    const handleHoursChange = e => {
        const value = Number(e.target.value)
        let newEscalationLevelsState = cloneDeep(escalationLevelsState)
        newEscalationLevelsState = newEscalationLevelsState.map(item => {
            if (
                item.level === level &&
                value >= hoursUntilAlertMin &&
                value <= 23
            ) {
                item.hoursUntilAlert = value
            }
            return item
        })
        setEscalationLevelsState(newEscalationLevelsState)
    }

    const handleReportTypesChange = (event, newValue) => {
        if (errorState.reportTypes) {
            const newErrorState = cloneDeep(errorState)
            newErrorState.reportTypes = false
            setErrorState({
                ...fullErrorState,
                [level]: newErrorState
            })
        }
        let newEscalationLevelsState = cloneDeep(escalationLevelsState)
        newEscalationLevelsState = newEscalationLevelsState.map(item => {
            if (item.level === level) {
                item.reportTypes = newValue.map(item => item.reportType)
            }
            return item
        })
        setEscalationLevelsState(newEscalationLevelsState)
    }

    const handleMessageChange = text => {
        if (errorState.message) {
            const newErrorState = cloneDeep(errorState)
            newErrorState.message = false
            setErrorState({
                ...fullErrorState,
                [level]: newErrorState
            })
        }
        let newEscalationLevelsState = cloneDeep(escalationLevelsState)
        newEscalationLevelsState = newEscalationLevelsState.map(item => {
            if (item.level === level) {
                item.message = text
            }
            return item
        })
        setEscalationLevelsState(newEscalationLevelsState)
    }

    const renderReportTypes = useMemo(() => {
        return reportTypes.map(reportType => {
            const name =
                Number(reportType) === 3
                    ? `${locale.devices.deviceLastReportTypeShortDescription[reportType]}, ${locale.devices.deviceLastReportTypeShortDescription['3-bait']}`
                    : locale.devices.deviceLastReportTypeShortDescription[
                          reportType
                      ]
            return {
                reportType,
                name
            }
        })
    }, [reportTypes, locale])

    useEffect(() => {
        const emailList =
            typeof email === 'string'
                ? email
                      .split(',')
                      .map(e => e.trim())
                      .filter(e => e !== '')
                : [email]
        setEmails(emailList)
        emailValidatorAndHandler(emailList)
    }, [])

    return (
        <Box className={classes.content}>
            <Box style={{ width: 200, marginRight: 20 }}>
                <Typography
                    className={classes.textBold}
                >{`${locale.locations.level} ${level}`}</Typography>

                <Typography>
                    {locale.locations.sendAutomaticallyTriggered}
                </Typography>
            </Box>
            <Box className={classes.formWrapper}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center'
                        }}
                    >
                        <TextField
                            variant="outlined"
                            style={{ width: 70 }}
                            type="number"
                            inputProps={{
                                min: daysUntilAlertMin
                            }}
                            onChange={handleDaysChange}
                            value={daysUntilAlert}
                        />
                        <Box
                            style={{
                                display: 'flex',
                                gap: 3,
                                alignItems: 'center'
                            }}
                        >
                            <Typography className={classes.textBold}>
                                {locale.locations.days}
                            </Typography>
                            <Tooltip
                                hover
                                placement="top"
                                text={locale.locations.afterTrapStatus}
                            >
                                <Info
                                    style={{ marginLeft: 10 }}
                                    size={18}
                                    color={theme.textSecondary}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                    <IconButton
                        style={{ marginTop: '-5px', marginLeft: 5 }}
                        edge="start"
                        color="inherit"
                        onClick={() => onDeleteLevel(level)}
                        aria-label="close"
                    >
                        <img src={RemoveIconSvg} alt="remove" />
                    </IconButton>
                </div>
                <Box className={classes.daysWrapper}>
                    <TextField
                        variant="outlined"
                        style={{ width: 70 }}
                        type="number"
                        inputProps={{
                            max: 23,
                            min: hoursUntilAlertMin
                        }}
                        onChange={handleHoursChange}
                        value={hoursUntilAlert}
                    />
                    <Box
                        style={{
                            display: 'flex',
                            gap: 3,
                            alignItems: 'center'
                        }}
                    >
                        <Typography className={classes.textBold}>
                            {locale.locations.hours}
                        </Typography>
                        <Tooltip
                            hover
                            placement="top"
                            text={locale.locations.afterTrapStatus}
                        >
                            <Info
                                style={{ marginLeft: 10 }}
                                size={18}
                                color={theme.textSecondary}
                            />
                        </Tooltip>
                    </Box>
                </Box>
                <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    defaultValue={[]}
                    value={emails}
                    onChange={(event, newValue) => {
                        updateEmailsAndErrors(newValue)
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const isValid = !emailErrors[index]
                            const chipClassName = !isValid
                                ? classes.shake
                                : classes.marginChip

                            return (
                                <Tooltip
                                    key={`${index}-${option}`}
                                    text={!isValid ? emailErrors[index] : ''}
                                    placement="top"
                                    hover
                                >
                                    <Chip
                                        variant="outlined"
                                        label={option}
                                        {...getTagProps({ index })}
                                        deleteIcon={
                                            <Cancel
                                                style={{
                                                    color: isValid
                                                        ? 'default'
                                                        : 'red'
                                                }}
                                            />
                                        }
                                        style={{
                                            borderColor: isValid
                                                ? 'default'
                                                : 'red',
                                            color: isValid ? 'default' : 'red'
                                        }}
                                        className={chipClassName}
                                    />
                                </Tooltip>
                            )
                        })
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant="outlined"
                            error={errorState?.email}
                            label={locale.locations.eMailOfRecipient}
                            onBlur={handleEmailInputChange}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    handleEmailInputChange(e)
                                }
                            }}
                        />
                    )}
                />

                <Autocomplete
                    multiple
                    options={reportTypesOptions}
                    getOptionLabel={option => option.name}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label={locale.reports.reports}
                            variant="outlined"
                            error={errorState?.reportTypes}
                            InputProps={{
                                ...{
                                    style: { borderRadius: theme.borderRadius }
                                },
                                ...params.InputProps
                            }}
                        />
                    )}
                    getOptionSelected={(option, value) => {
                        return option.reportType === value.reportType
                    }}
                    renderOption={(option, { selected }) => {
                        return (
                            <React.Fragment>
                                <Checkbox
                                    icon={checkboxIcon}
                                    checkedIcon={checkedCheckboxIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.name}
                            </React.Fragment>
                        )
                    }}
                    value={renderReportTypes || []}
                    onChange={handleReportTypesChange}
                    noOptionsText={locale.common.nothingWasFound}
                />

                <Input
                    label={locale.reports.message}
                    multiline={true}
                    rows={4}
                    isError={errorState?.message}
                    setValue={handleMessageChange}
                    value={message}
                />
            </Box>
        </Box>
    )
}
